import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import {
    Card,
    CardBody,
    Button,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
} from "reactstrap";
import { getActiveCompany, getActiveGeographicalCoverByTypeAndCompany, getActiveService, getActiveTypeByCompanyId, getAllActivePlans, getCompanyByCategoryIdWithPlan, } from "../../../../api";
import { getFromLocalStorage } from "../../../../hooks/useLocalStorage";
import { Travel } from "../../../../constant";
import TravelSummary from "./travel_summary";
import MembershipFee from "./MembershipFee";
import { toast } from "react-toastify";
import AddonDetails from "../policy-create/addon-details-modal";
import { set } from "lodash";
import InsuranceBenefit from "../policy-create/insurance-benefit-modal";
import travellerImage from "../../../../assets/images/traveller.png";
import "./Style.css";



const DROPDOWN_CONFIG = [
    {
        key: 'scheme',
        label: 'Scheme',
        placeholder: 'Choose a scheme',
        dependsOn: null,

    },
    {
        key: 'documentType',
        label: 'Type Of Document',
        placeholder: 'Select Type Of Document',
        dependsOn: null,

    },
    {
        key: 'geographicalCover',
        label: 'Geographical Cover',
        placeholder: 'Choose geographical cover',
        dependsOn: null,

    },
];

const SelectComponent = ({ name, label, options, value, onChange, disabled, placeholder }) => {
    const selectedOption = options.find(option => option.value === (value?.value || value)) || null;

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            borderRadius: "10px",
            minHeight: "10px",
            borderColor: state.isFocused ? "#7fb9ff" : "#d0d8e6",
            boxShadow: state.isFocused ? "0 0 0 1px #7fb9ff" : "none",
            backgroundColor: "#ffffff",
        }),
        singleValue: (base) => ({
            ...base,
            color: "#1e293b",
        }),
        placeholder: (base) => ({
            ...base,
            color: "#64748b",
        }),
        input: (base) => ({
            ...base,
            color: "#1e293b",
        }),
        menu: (base) => ({
            ...base,
            zIndex: 20,
            backgroundColor: "#ffffff",
            fontSize: "13px",
        }),
        option: (base, state) => ({
            ...base,
            color: state.isSelected ? "#ffffff" : "#1e293b",
            backgroundColor: state.isSelected ? "#0d7ff2" : state.isFocused ? "#eaf4ff" : "#ffffff",
        }),
    };

    const handleChange = (selectedOption) => {
        onChange({ target: { name, value: selectedOption } });
    };

    return (
        <FormGroup>
            <Label for={name} style={{fontSize:"12px"}}>{label}</Label>
            <Select
                id={name}
                name={name}
                value={selectedOption}
                onChange={handleChange}
                options={options}
                isDisabled={disabled}
                placeholder={"Select"}
                className="react-select"
                classNamePrefix="react-select"
                styles={selectStyles}
            />
        </FormGroup>
    );
};

const YesNoField = ({ name, label, value, onChange, error }) => {
    const options = ["Yes", "No"];

    return (
        <FormGroup>
            <Label className="d-block">{label}</Label>
            <div className="d-flex gap-2 flex-wrap">
                {options.map((option) => {
                    const isSelected = value === option;
                    const activeBg = "#0d7ff2";
                    const activeBorder = "#0052a3";

                    return (
                        <Button
                            key={option}
                            type="button"
                            className="health-declaration-toggle-btn"
                            onClick={() => onChange({ target: { name, value: option } })}
                            style={{
                                "--hd-btn-bg": isSelected ? activeBg : "#ffffff",
                                "--hd-btn-border": isSelected ? activeBorder : "#7aa6d9",
                                "--hd-btn-color": isSelected ? "#ffffff" : "#1f3b63",
                                "--hd-btn-shadow": isSelected
                                    ? `inset 0 0 0 1px ${activeBorder}`
                                    : "inset 0 0 0 1px #7aa6d9",
                                minWidth: "50px",
                                borderRadius: "999px",
                                fontWeight: 600,
                                borderWidth: "2px",
                                borderStyle: "solid",
                                transition: "all 0.2s ease",
                                padding: "6px 14px",
                                fontSize: "14px",
                            }}
                        >
                            {option}
                        </Button>
                    );
                })}
            </div>
            {error && <div className="text-danger small mt-1">{error}</div>}
        </FormGroup>
    );
};



const getPlanCostOption = (plan, cover) => {
    if (!plan) {
        return null;
    }

    return plan.plan_costs?.find((option) => String(option.cover) === String(cover)) || null;
};

const normalizeAddonOption = (selectedOption, typeOfAddon = null) => {
    const addOnTypeRaw = selectedOption.add_on_type || selectedOption.addOnType || selectedOption?.addOn?.type || selectedOption?.type || typeOfAddon;
    const addOnType = typeof addOnTypeRaw === 'string'
        ? addOnTypeRaw.toLowerCase()
        : (addOnTypeRaw?.type ? String(addOnTypeRaw.type).toLowerCase() : undefined);

    const addonValueTypeRaw = selectedOption.addon_value_type || selectedOption.addonValueType || selectedOption?.value_type;
    const addonValueType = typeof addonValueTypeRaw === 'string'
        ? addonValueTypeRaw.toLowerCase()
        : String(addonValueTypeRaw || '').toLowerCase();

    const addonCoaValue = selectedOption.addon_coa_value ?? selectedOption.addonCoaValue ?? selectedOption.coa_value ?? selectedOption.percentage ?? 0;
    const addonCoiValue = selectedOption.addon_coi_value ?? selectedOption.addonCoiValue ?? selectedOption.coi_value ?? 0;
    const optionValueId = selectedOption.value ?? selectedOption.service_id ?? selectedOption?.service?.id;
    const truncatedLabel = selectedOption.label.length > 30
        ? `${selectedOption.label.substring(0, 30)}...`
        : selectedOption.label;

    return {
        addOnTypeRaw,
        addOnType,
        addonValueTypeRaw,
        addonValueType,
        addonCoaValue: Number(addonCoaValue) || 0,
        addonCoiValue: Number(addonCoiValue) || 0,
        optionValueId,
        updatedSelectedOption: {
            ...selectedOption,
            add_on_type: addOnTypeRaw || selectedOption.add_on_type,
            addon_value_type: addonValueTypeRaw || selectedOption.addon_value_type,
            addon_coa_value: Number(addonCoaValue) || 0,
            addon_coi_value: Number(addonCoiValue) || 0,
            value: optionValueId,
            label: truncatedLabel,
        },
    };
};

const calculateAddonAmounts = ({ base = 0, coiCost = 0, addonValueType = '', addonCoaValue = 0, addonCoiValue = 0 }) => {
    if (addonValueType === 'fixed') {
        return {
            addonCost: Number(addonCoaValue) || 0,
            addonCOICost: 0,
        };
    }

    return {
        addonCost: (Number(base) * Number(addonCoaValue || 0)) / 100,
        addonCOICost: (Number(coiCost) * Number(addonCoiValue || 0)) / 100,
    };
};

const calculateAgeFromDob = (dob) => {
    if (!dob) return "";

    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) return "";

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1;
    }

    return age >= 0 ? String(age) : "";
};

const PremiumCalculator = () => {
    const currentRole = getFromLocalStorage("ROLE");
    const [isViewInsurancebenefit, setViewInsuranceBenefit] = useState(false);
    const [isTravelSummaryVisible, setIsTravelSummaryVisible] = useState(false);
    const [isPlanComponentVisible, setIsPlanComponentVisible] = useState(true);
    const [form, setForm] = useState({
        scheme: "",
        documentType: null,
        geographicalCover: "",
        travelDays: "",
        dob: "",
        amtDays: "",
        startDate: "",
        endDate: "",
        criticalIllness: "",
        preExistingDisease: "",
        age: "",
    });
    const [errors, setErrors] = useState({});
    const [result, setResult] = useState(null);
    const [options, setOptions] = useState({
        scheme: [],
        documentType: [],
        geographicalCover: [],
        service: [],
        insuranceBenefits: [],
    });
    const [plans, setPlans] = useState([]);
    const [travellerage, setTravellerAge] = useState("");
    const [planCount, setPlanCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [plansByPage, setPlansByPage] = useState({});
    const [isPlansLoading, setIsPlansLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedServices, setSelectedServices] = useState({});
    const [selectedPlanState, setSelectedPlanState] = useState({
        plan_id: null,
        title: "",
        cover: "",
        cost: 0,
        basic_cost: 0,
        coi: "",
        addon_cost: 0,
        showError: false,
        planDetails: [],
        totalWithGST: 0,
        currency: "INR",
        discountFinalPricing: null,
    });
    const [addonSelectedId, setAddonSelectedId] = useState({});
    const [individualAddonCosts, setIndividualAddonCosts] = useState({});
    const [planAddonInputs, setPlanAddonInputs] = useState({});
    const [additionalCost, setAdditionalCost] = useState({});
    const [planAddons, setPlanAddons] = useState({});
    const [addonInsuranceSelectedId, setAddonInsuranceSelectedId] = useState({});
    const [additionalCOICost, setAdditionalCOICost] = useState({});
    const [additionalCOIPercentage, setAdditionalCOIPercentage] = useState({});
    const [planCost, setPlanCost] = useState(0);
    const [planCOICost, setPlanCOICost] = useState(0);
    const [coiCost, setCoiCost] = useState(0);
    const [baseCost, setBaseCost] = useState(0);
    const [viewAddonDetailModal, setViewAddonDetailModal] = useState(false);
    const [selectedAddonDetails, setSelectedAddonDetails] = useState([]);
    const [individualAddonCostsBYPlanId, setIndividualAddonCostsBYPlanId] = useState({});
    const [selectedPlanIdForAddon, setSelectedPlanIdForAddon] = useState(null);
    const [additionalCOACost, setAdditionalCOACost] = useState(0);
    const [coveragesPricingArray, setCoveragesPricingArray] = useState([]); // Array to store pricing for 6 coverages
    const todayDate = useMemo(() => new Date().toISOString().split("T")[0], []);
    const plansById = useMemo(() => plans.reduce((acc, plan) => {
        acc[plan.id] = plan;
        return acc;
    }, {}), [plans]);
    const isAmtType = useMemo(() => {
        const typeLabel = String(form.documentType?.label || "").toLowerCase();
        const typeValue = String(form.documentType?.value || "").toLowerCase();
        return typeLabel === "amt" || typeValue === "amt";
    }, [form.documentType]);

    // Derived pricing summary with clean key names (safe aliases, no flow changes)
    const pricingSummary = useMemo(() => ({
        planCost: selectedPlanState.basic_cost || 0,
        discountPercent: selectedPlanState.discountFinalPricing?.discount_percent || 0,
        discountAmount: selectedPlanState.discountFinalPricing?.discount_amount || 0,
        finalPlanCost: selectedPlanState.discountFinalPricing?.new_base_premium || selectedPlanState.basic_cost || 0,
        totalAddonCost: selectedPlanState.addon_cost || 0,
        totalPlanCost: selectedPlanState.discountFinalPricing?.total_premium || 0,
        gstAmount: selectedPlanState.discountFinalPricing?.gst_amount || 0,
        totalCost: selectedPlanState.totalWithGST || 0,
    }), [selectedPlanState]);

    // Function to calculate pricing for next 6 coverages using exact formula
    const calculateCoveragesPricing = useCallback((selectedPlanId, discountData, addonCostPerPlan) => {
        if (!selectedPlanId || !plansById[selectedPlanId]) {
            console.warn('[calculateCoveragesPricing] No valid plan selected');
            setCoveragesPricingArray([]);
            return;
        }

        const plan = plansById[selectedPlanId];
        const planCostOptions = Array.isArray(plan.plan_costs) ? plan.plan_costs : [];

        if (planCostOptions.length === 0) {
            console.warn('[calculateCoveragesPricing] No plan cost options available');
            setCoveragesPricingArray([]);
            return;
        }

        // Get first 6 coverages
        const coveragesToProcess = planCostOptions.slice(0, 6);

        const calculatedCoverages = coveragesToProcess.map((costOption, index) => {
            const basePlanCost = Number(costOption.cost || 0);
            const discountPercent = Number(discountData?.discount_percent || 0);
            const discountAmount = Number((basePlanCost * discountPercent) / 100 || 0);

            // Formula: Final Plan Cost = Plan Cost − Discount
            const finalPlanCost = Number((basePlanCost - discountAmount).toFixed(2));

            // Total Addon Cost (same for all coverages in this plan)
            const totalAddonCost = Number(addonCostPerPlan || 0);

            // Formula: Total Plan Cost = Final Plan Cost + Total Addon Cost
            const totalPlanCost = Number((finalPlanCost + totalAddonCost).toFixed(2));

            // Formula: GST = Total Plan Cost × 18%
            const gstAmount = Number((totalPlanCost * 0.18).toFixed(2));

            // Formula: Total Cost = Total Plan Cost + GST
            const totalCost = Number((totalPlanCost + gstAmount).toFixed(2));

            return {
                coverageIndex: index,
                cover: costOption.cover,
                planCost: basePlanCost,
                discountPercent: discountPercent,
                discountAmount: Number(discountAmount.toFixed(2)),
                finalPlanCost: finalPlanCost,
                totalAddonCost: totalAddonCost,
                totalPlanCost: totalPlanCost,
                gstAmount: gstAmount,
                totalCost: totalCost,
            };
        });

        console.log('[calculateCoveragesPricing] Calculated coverages:', calculatedCoverages);
        setCoveragesPricingArray(calculatedCoverages);
    }, [plansById]);

    // Effect to trigger coverages pricing calculation whenever plan, discount, or addon changes
    useEffect(() => {
        if (!selectedPlan) {
            setCoveragesPricingArray([]);
            return;
        }

        const addonCostValue = selectedPlanState.addon_cost || additionalCost[selectedPlan] || 0;
        const discountData = selectedPlanState.discountFinalPricing || {};

        console.log('[coveragesPricingEffect] Triggering calculation', {
            selectedPlan,
            discountData,
            addonCostValue,
        });

        calculateCoveragesPricing(selectedPlan, discountData, addonCostValue);
    }, [selectedPlan, selectedPlanState.discountFinalPricing, selectedPlanState.addon_cost, additionalCost, calculateCoveragesPricing]);

    useEffect(() => {
         if (currentRole == "HO") {
            fetchDropdownOptions(getActiveCompany, 'scheme');
        } else {
            // Fetch all companies/schemes without category filter to show all options
            fetchDropdownOptions(getActiveCompany(), 'scheme');
        }
       // currentRole == "HO" ? 
      //  fetchDropdownOptions(getActiveCompany, 'scheme') 
        //: fetchDropdownOptions(getCompanyByCategoryIdWithPlan(1), 'scheme');
        fetchDropdownOptions(getActiveService({ insurance_type: "addons", }), 'service');

    }, []);
    console.log("Coverages Pricing Array:", coveragesPricingArray);
    const fetchDropdownOptions = async (api, key) => {
        // Simulate API call to fetch dropdown options if needed
        try {
            // Fetch options and update state if necessary
            const options = typeof api === 'function' ? await api() : await api; // Supports both function refs and promises
            console.debug("Fetched options for", key, options);
            let processedOptions = [];
            // if (key === 'scheme') {
            //     // Process options for scheme dropdown if needed
            //     processedOptions = options?.data?.result?.sort((a, b) => a.company_id - b.company_id).map(option => {
            //         if (option?.Company) {
            //             return {
            //                 label: option.Company.client_company_alias,
            //                 value: option.Company.company_id,
            //                 name: option.Company.client_company_name,
            //             };
            //         }
            //         return null;
            //     }).filter(Boolean);

            //     //  console.debug("Processed scheme options:", processedOptions);

            // }
             if (key === 'scheme') {
                // Process options for scheme dropdown
                // Handle both direct company objects and Company nested objects
                processedOptions = options?.data?.result?.sort((a, b) => {
                    const idA = a.company_id || a.id || 0;
                    const idB = b.company_id || b.id || 0;
                    return idA - idB;
                }).map(option => {
                    // Check if Company object exists (nested structure)
                    if (option?.Company) {
                        return {
                            label: option.Company?.client_company_alias || option.Company?.client_company_name,
                            value: option.Company?.company_id,
                            name: option.Company?.client_company_name,
                        };
                    }
                    // Direct company object
                    if (option?.client_company_alias || option?.client_company_name) {
                        return {
                            label: option.client_company_alias || option.client_company_name,
                            value: option.company_id || option.id,
                            name: option.client_company_name,
                        };
                    }
                    // Fallback for other structures
                    if (option?.alias || option?.name) {
                        return {
                            label: option.alias || option.name,
                            value: option.company_id || option.id,
                            name: option.name,
                        };
                    }
                    return null;
                }).filter(Boolean);

                console.debug("Processed scheme options:", processedOptions);

            }
            if (key === 'documentType') {
                // Process options for document type dropdown if needed
                processedOptions = options?.data?.result?.map(option => {
                    if (option?.type) {
                        return {
                            label: option.type.title,
                            value: option?.type?.id,

                        };
                    }
                    return null;
                }).filter(Boolean);

            }
            if (key === 'geographicalCover') {
                // Process options for geographical cover dropdown if needed
                processedOptions = options?.data?.result?.map(option => {
                    if (option?.geographical_cover) {
                        return {
                            label: option.geographical_cover.name,
                            value: option?.geographical_cover?.id,

                        };
                    }
                    return null;
                }).filter(Boolean);
            }
            if (key === 'service') {
                processedOptions = options?.data?.result?.map(item => ({ value: item.id, label: item.title, percentage: item.percentage }));
            }
            setOptions(prev => ({ ...prev, [key]: processedOptions }));
        }
        catch (error) {
            console.error("Error fetching dropdown options:", error);
        }
    }

    const computeEndDate = (startDate, travelDays) => {
        if (!startDate || !travelDays) return "";
        const days = parseInt(travelDays, 10);
        if (isNaN(days) || days < 1) return "";
        const start = new Date(startDate);
        if (isNaN(start.getTime())) return "";
        start.setDate(start.getDate() + days - 1);
        return start.toISOString().split("T")[0];
    };

    const computeTravelDays = (startDate, endDate) => {
        if (!startDate || !endDate) return "";
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return "";
        const millisecondsPerDay = 1000 * 60 * 60 * 24;
        return String(Math.floor((end - start) / millisecondsPerDay) + 1);
    };

    const computeAmtEndDate = (startDate) => {
        if (!startDate) return "";
        const start = new Date(startDate);
        if (isNaN(start.getTime())) return "";
        start.setDate(start.getDate() + 365);
        return start.toISOString().split("T")[0];
    };

    const resetTravelAndHealthFields = (baseForm) => ({
        ...baseForm,
        travelDays: "",
        dob: "",
        amtDays: "",
        startDate: "",
        endDate: "",
        criticalIllness: "",
        preExistingDisease: "",
    });

    const handleChange = async (e) => {
        const { name, value } = e.target;
        let newForm = { ...form };

        // For dropdowns, value is an object; for inputs, it's a string
        if (typeof value === 'object' && value !== null) {
            newForm[name] = value;
        } else {
            newForm[name] = value;
        }

        const selectedTypeLabel = String(newForm.documentType?.label || "").toLowerCase();
        const selectedTypeValue = String(newForm.documentType?.value || "").toLowerCase();
        const isAmtSelected = selectedTypeLabel === "amt" || selectedTypeValue === "amt";

        if (name === "startDate") {
            if (isAmtSelected) {
                const validStartDate = value || todayDate;
                newForm.startDate = validStartDate;
                newForm.endDate = computeAmtEndDate(validStartDate);
                newForm.travelDays = "365";
            } else if (newForm.endDate) {
                const calculatedDays = computeTravelDays(value, newForm.endDate);
                newForm.travelDays = calculatedDays || newForm.travelDays;
            } else if (newForm.travelDays) {
                const calculatedEndDate = computeEndDate(value, newForm.travelDays);
                if (calculatedEndDate) newForm.endDate = calculatedEndDate;
            }
        } else if (name === "travelDays") {
            if (!isAmtSelected) {
                const calculatedEndDate = computeEndDate(newForm.startDate, value);
                if (calculatedEndDate) {
                    newForm.endDate = calculatedEndDate;
                } else if (!value) {
                    newForm.endDate = "";
                }
            }
        } else if (name === "endDate") {
            if (!isAmtSelected) {
                const calculatedDays = computeTravelDays(newForm.startDate, value);
                if (calculatedDays) {
                    newForm.travelDays = calculatedDays;
                } else if (!value) {
                    newForm.travelDays = "";
                }
            }
        } else if (name === "dob") {
            newForm.age = calculateAgeFromDob(value);
        }

        if (name === "scheme") {
            newForm = resetTravelAndHealthFields(newForm);
            newForm.documentType = "";
            newForm.geographicalCover = "";
            newForm.amtDays = "";
            if (newForm.scheme?.value) {
                await fetchDropdownOptions(getActiveTypeByCompanyId(newForm.scheme.value), 'documentType');
            }
        } else if (name === "documentType") {
            newForm = resetTravelAndHealthFields(newForm);
            newForm.geographicalCover = "";

            const currentTypeLabel = String(newForm.documentType?.label || "").toLowerCase();
            const currentTypeValue = String(newForm.documentType?.value || "").toLowerCase();
            const currentIsAmt = currentTypeLabel === "amt" || currentTypeValue === "amt";

            if (!currentIsAmt) {
                newForm.amtDays = "";
            } else {
                const amtStartDate = newForm.startDate || todayDate;
                newForm.startDate = amtStartDate;
                newForm.endDate = computeAmtEndDate(amtStartDate);
                newForm.travelDays = "365";
            }

            if (newForm.documentType?.value && newForm.scheme?.value) {
                await fetchDropdownOptions(getActiveGeographicalCoverByTypeAndCompany({ typeId: newForm.documentType.value, companyId: newForm.scheme.value }), 'geographicalCover');
            }
        } else if (name === "geographicalCover") {
            newForm = resetTravelAndHealthFields(newForm);

            const currentTypeLabel = String(newForm.documentType?.label || "").toLowerCase();
            const currentTypeValue = String(newForm.documentType?.value || "").toLowerCase();
            const currentIsAmt = currentTypeLabel === "amt" || currentTypeValue === "amt";

            if (currentIsAmt) {
                const amtStartDate = todayDate;
                newForm.startDate = amtStartDate;
                newForm.endDate = computeAmtEndDate(amtStartDate);
                newForm.travelDays = "365";
            }
        }

        setForm(newForm);
        setResult(null);
        setErrors({});
    };
    console.log("Current form state:", form);
    const validateForm = () => {
        const newErrors = {};
        if (!form.scheme?.value) newErrors.scheme = "Scheme is required.";
        if (!form.documentType?.value) newErrors.documentType = "Type of document is required.";
        if (!form.geographicalCover?.value) newErrors.geographicalCover = "Geographical cover is required.";
        if (!form.travelDays) newErrors.travelDays = "Travel days is required.";
        if (!form.dob) newErrors.dob = "DOB is required.";
        if (!form.startDate) newErrors.startDate = "Start date is required.";
        if (!form.endDate) newErrors.endDate = "End date is required.";
        if (!form.criticalIllness) newErrors.criticalIllness = "Critical Illness is required.";
      //  if (!form.preExistingDisease) newErrors.preExistingDisease = "Pre-existing Diseases is required.";
        if (form.startDate && form.endDate) {
            const startDate = new Date(form.startDate);
            const endDate = new Date(form.endDate);
            if (endDate < startDate) {
                newErrors.endDate = "End date cannot be before start date.";
            }
        }
        if (isAmtType && !form.amtDays) newErrors.amtDays = "AMT days are required for AMT plans.";
        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setResult(null);
            return;
        }
        setErrors({});
        setIsTravelSummaryVisible(true);
        setIsPlanComponentVisible(true);
        setCurrentPage(1);
        setSearchQuery("");
        setPlansByPage({});
        fetchPlan(1, { forceRefresh: true, searchQuery: "" });
    };
    const fetchPlan = async (pageIndex = 1, options = {}) => {
        const { forceRefresh = false, searchQuery: searchQueryOverride } = options;
        const effectiveSearchQuery = typeof searchQueryOverride === "string" ? searchQueryOverride : searchQuery;
        const normalizedSearchQuery = effectiveSearchQuery.trim();
        const cacheKey = `${normalizedSearchQuery.toLowerCase() || "__all__"}::${pageIndex}`;
        const cachedEntry = plansByPage[cacheKey];

        if (!forceRefresh && cachedEntry?.plans) {
            setPlans(cachedEntry.plans);
            setPlanCount(cachedEntry.totalCount || 0);
            return;
        }

        setIsPlansLoading(true);

        const data = {
            journey_start_date: form.startDate,
            typeId: Number(form.documentType?.value),
            geographicalCoverId: Number(form.geographicalCover?.value),
            age: 0,
            travelDays: Number(form.travelDays) || 0,
            trip_duration: Number(form.amtDays) || 0,
            companyId: Number(form.scheme?.value) || 0,
            ped: form.preExistingDisease || false,
            CI: form.criticalIllness || false,
            search_query: normalizedSearchQuery,
            pageSize: pageIndex,
            limit: 12,
            traveller_dob: [form.dob],
            current_role: currentRole?.alias

        };
        try {
            const response = await getAllActivePlans(data);
            const fetchedPlans = response?.data?.result?.fetchPlan || [];
            const totalCount = response?.data?.result?.totalCount || 0;
            console.debug("Fetched plans:", fetchedPlans);
            setPlans(fetchedPlans);
            setPlansByPage((prev) => ({
                ...prev,
                [cacheKey]: {
                    plans: fetchedPlans,
                    totalCount,
                },
            }));
            setTravellerAge(response?.data?.result?.age || "");
            setForm((prev) => ({ ...prev, age: response?.data?.result?.age || "" }));
            setPlanCount(totalCount);
        } catch (error) {
            console.error("Error fetching plan:", error);
        } finally {
            setIsPlansLoading(false);
        }
    };

    const handlePlanSearch = useCallback((queryText = "") => {
        const normalizedQuery = String(queryText || "").trim();

        setSearchQuery(normalizedQuery);
        setCurrentPage(1);
        fetchPlan(1, { forceRefresh: true, searchQuery: normalizedQuery });
    }, [fetchPlan]);

    const handlePageChange = useCallback((nextPage) => {
        const safePage = Number(nextPage) || 1;
        if (safePage === currentPage) return;

        setCurrentPage(safePage);
        fetchPlan(safePage);
    }, [currentPage, fetchPlan]);
    const handleReset = () => {
        setForm({
            scheme: "",
            documentType: "",
            geographicalCover: "",
            travelDays: "",
            dob: "",
            age: "",
            amtDays: "",
            startDate: "",
            endDate: "",
            criticalIllness: "",
            preExistingDisease: "",
        });
        setErrors({});
        setResult(null);
    };

    const handleSelectPlan = (
        planId,
        title = "",
        cover = "",
        totalWithGST = 0,
        cost = 0,
        addonCost = 0,
        basicCost = 0,
        coi = "",
        currency = "INR",
        discountFinalPricing = null,
    ) => {
        setSelectedPlan(planId);
        setSelectedPlanState((prev) => ({
            ...prev,
            plan_id: planId,
            title: title || "",
            cover: cover || "",
            cost: Number(cost || 0),
            basic_cost: Number(basicCost || cost || 0),
            coi: coi || "",
            addon_cost: Number(addonCost || 0),
            totalWithGST: Number(totalWithGST || 0),
            currency: currency || "INR",
            discountFinalPricing: discountFinalPricing || null,
        }));
    };

    const resetPlanValues = useCallback(() => {
        setPlans([]);
        setPlanCount(0);
        setCurrentPage(1);
        setPlansByPage({});
        setIsPlansLoading(false);
        setSelectedPlan(null);
        setSelectedServices({});
        setSelectedPlanState({
            plan_id: null,
            title: "",
            cover: "",
            cost: 0,
            basic_cost: 0,
            coi: "",
            addon_cost: 0,
            showError: false,
            planDetails: [],
            totalWithGST: 0,
            currency: "INR",
            discountFinalPricing: null,
        });
        setAddonSelectedId({});
        setAddonInsuranceSelectedId({});
        setIndividualAddonCosts({});
        setIndividualAddonCostsBYPlanId({});
        setPlanAddonInputs({});
        setAdditionalCost({});
        setAdditionalCOICost({});
        setAdditionalCOIPercentage({});
        setAdditionalCOACost(0);
        setSelectedAddonDetails([]);
        setSelectedPlanIdForAddon(null);
        setCoveragesPricingArray([]);
        setSearchQuery("");
        setCurrentPage(1);
        setPlansByPage({});
        setIsPlanComponentVisible(false);
        setIsTravelSummaryVisible(false);
    }, []);
    const handleCoverChange = useCallback((planId, cover, plan122, age = 0) => {
        const plan = plansById[planId];
        if (!plan) return;

        const selectedOption = getPlanCostOption(plan, cover);
        if (!selectedOption) return;
        const currentAddons = planAddons[planId] || [];
        const newAddonCost = currentAddons.reduce((total, addon) => {
            const percentage = addon.percentage || 0;
            return total + (Number(selectedOption.cost) * percentage) / 100;
        }, 0);



        setAddonInsuranceSelectedId({})
        setAddonSelectedId({})
        setSelectedServices({})
        setAdditionalCost({})
        setAdditionalCOICost({})
        setAdditionalCOIPercentage({})
        setIndividualAddonCosts({})
        setSelectedPlanState(prev => ({
            ...prev,
            plan_id: planId,
            title: plan.title,
            cover: selectedOption.cover,
            // Store base cost only; do NOT include addons or discounts here
            cost: Number(selectedOption.cost),
            basic_cost: Number(selectedOption.cost),
            coi: selectedOption.coi,
            addon_cost: newAddonCost,
            showError: false,
            planDetails: plan,
        }));
        // setSelectedAddonDetails([])

        console.log(planId, 'inside handlecoverchange')

    }, [planAddons, plansById]);
    const handleAddonSelectInd = useCallback((selectedOption, planId, cover, planCost, planCOICost, typeOfAddon = null, age = 0) => {
        if (selectedOption) {
            const activePlan = plansById[planId];
            const selectedCostOption = getPlanCostOption(activePlan, cover);
            let base = planCost || selectedCostOption?.cost || 0;
            let coiCost = planCOICost || selectedCostOption?.coi || 0;
            setCoiCost(coiCost);
            setBaseCost(base);
            const {
                addOnTypeRaw,
                addOnType,
                addonValueType,
                addonCoaValue,
                addonCoiValue,
                optionValueId,
                updatedSelectedOption,
            } = normalizeAddonOption(selectedOption, typeOfAddon);

            if (addOnType === "insurance" || typeOfAddon === "insurance") {
                console.log("addonInsuranceSelectedId", addonInsuranceSelectedId);
                const servicesForPlan = addonInsuranceSelectedId[planId] || null;
                const indexToAddon = servicesForPlan && servicesForPlan.find(s => s === optionValueId);
                console.log("servicesForPlan", servicesForPlan, "selectedOption.value", selectedOption, "indexToAddon", indexToAddon);
                const isInsuranceAlreadySelected = indexToAddon > 0 ? true : false;
                if (isInsuranceAlreadySelected) {
                    toast.error("Only one insurance addon can be selected per plan.");
                    return; // Exit the function if insurance addon is already selected
                }
                else {
                    setAddonInsuranceSelectedId((prevState) => ({
                        ...prevState,
                        [planId]: prevState[planId]
                            ? [...prevState[planId], optionValueId] // Add to the existing array
                            : [optionValueId], // Initialize array if not existing
                    }));
                }
            }
            setAddonSelectedId(prevState => ({
                ...prevState,
                [planId]: optionValueId
            }));

            let selectedAddonPercentage = Number(addonCoaValue) || 0;
            let selectedAddonCOIPercentage = Number(addonCoiValue) || 0;
            const { addonCost, addonCOICost } = calculateAddonAmounts({
                base,
                coiCost,
                addonValueType,
                addonCoaValue: selectedAddonPercentage,
                addonCoiValue: selectedAddonCOIPercentage,
            });

            setSelectedServices((prevState) => ({
                ...prevState,
                [planId]: prevState[planId]
                    ? [...prevState[planId], updatedSelectedOption] // Add to the existing array
                    : [updatedSelectedOption], // Initialize array if not existing
            }));
            // Update additional cost for the selected plan
            setAdditionalCost((prevCost) => ({
                ...prevCost,
                [planId]: (prevCost[planId] || 0) + addonCost, // Increment the additional cost
            }));

            setAdditionalCOICost((prevCost) => ({
                ...prevCost,
                [planId]: (prevCost[planId] || 0) + addonCOICost, // Increment the additional cost
            }));
            setAdditionalCOIPercentage((prevCost) => ({
                ...prevCost,
                [planId]: (prevCost[planId] || 0) + selectedAddonCOIPercentage, // Increment the additional cost
            }));

            setIndividualAddonCosts((prevCosts) => ({
                ...prevCosts,
                [planId]: {
                    ...prevCosts[planId],
                    [`${optionValueId}+${addOnType}`]: { addonCost, addonCOICost, selectedAddonPercentage, selectedAddonCOIPercentage }, // Store the addon cost using the add-on ID as the key
                },
            }));

            toast.success("Add-on added successfully");


        }
    }, [addonInsuranceSelectedId, plansById]);
    const toggleViewAddonDetailModal = useCallback((
        selectedVal,
        individualCosts,
        planId,
        additionalCostValue,
        planCostValue,
        planCOICostValue
    ) => {
        // Resolve safe values with fallbacks to current state by plan id
        const safePlanId = planId ?? selectedPlanIdForAddon;
        const safeSelectedVal = Array.isArray(selectedVal)
            ? selectedVal
            : (safePlanId != null ? (selectedServices?.[safePlanId] || []) : []);
        const safeIndividualCosts = individualCosts || {};
        const safeAdditionalCOA = Number(additionalCostValue ?? (safePlanId != null ? additionalCost?.[safePlanId] : 0)) || 0;
        const safePlanCost = Number(planCostValue ?? (safePlanId != null ? selectedPlanState?.[safePlanId]?.cost : 0)) || 0;
        const safePlanCOICost = Number(planCOICostValue ?? (safePlanId != null ? selectedPlanState?.[safePlanId]?.coi : 0)) || 0;
        console.log("Toggling Addon Detail Modal with:", { safeIndividualCosts, safeAdditionalCOA, safePlanCost, safePlanCOICost, safePlanId, safeSelectedVal });
        setSelectedAddonDetails(safeSelectedVal);
        setIndividualAddonCostsBYPlanId(prev => ({
            ...(prev || {}),
            [safePlanId]: safeIndividualCosts,
        }));
        setSelectedPlanIdForAddon(safePlanId);
        setAdditionalCOACost(safeAdditionalCOA);
        setPlanCost(safePlanCost);
        setPlanCOICost(safePlanCOICost);
        setViewAddonDetailModal(true);
    }, [additionalCost, selectedPlanIdForAddon, selectedPlanState, selectedServices]);
    const removeAddonInd = useCallback((service, planId, planCost) => {

        const serviceAddonCosts = individualAddonCosts[planId] || {};
        //  console.log("setSelectedServices", selectedServices);
        // console.log("serviceAddonCosts", serviceAddonCosts, "service", service , "individualAddonCosts", individualAddonCosts);
        // console.log("additionalCost", additionalCost, "additionalCOICost", additionalCOICost ,"additionalCOIPercentage", additionalCOIPercentage);

        if (service?.add_on_type?.toLowerCase() === "insurance") {
            console.log("addonInsuranceSelectedId", addonInsuranceSelectedId);

            setAddonInsuranceSelectedId((prevState) => {
                const services = [...prevState[planId]]; // clone the array
                const indexToRemove = services.findIndex(s => s == service.value);

                if (indexToRemove !== -1) {
                    services.splice(indexToRemove, 1); // remove only one instance
                }

                return {
                    ...prevState,
                    [planId]: services,
                };
            });
        }
        const serviceKey = `${service.value}+${service.add_on_type?.toLowerCase()}`;
        const addonCost = serviceAddonCosts[serviceKey]?.addonCost || 0;
        const selectedAddonCOIPercentage = serviceAddonCosts[serviceKey]?.selectedAddonCOIPercentage || 0;
        const addonCOICost = serviceAddonCosts[serviceKey]?.addonCOICost || 0;



        setSelectedServices((prevState) => {
            const services = [...prevState[planId]]; // clone the array
            const indexToRemove = services.findIndex(s => s.value === service.value && s.add_on_type?.toLowerCase() === service.add_on_type?.toLowerCase());

            if (indexToRemove !== -1) {
                services.splice(indexToRemove, 1); // remove only one instance
            }

            return {
                ...prevState,
                [planId]: services,
            };
        });

        // Update additional cost for the selected plan
        setAdditionalCost((prevCost) => ({
            ...prevCost,
            [planId]: (prevCost[planId] || 0).toFixed(2) - addonCost, // Decrease the additional cost
        }));

        // console.log("addonCost", addonCost, "addonCOICost11111111", addonCOICost ,"additionalCOICost", additionalCOICost);

        if (service.add_on_type?.toLowerCase() === "insurance") {

            setAdditionalCOICost((prevCost) => ({
                ...prevCost,
                [planId]: (prevCost[planId] || 0).toFixed(2) - addonCOICost, // Decrease the additional COI cost
            }));

            setAdditionalCOIPercentage((prevCost) => ({
                ...prevCost,
                [planId]: (prevCost[planId] || 0) - selectedAddonCOIPercentage, // Decrease the additional COI percentage
            }));
        }
        // Remove the addon cost entry
        toast.success("Add-on removed successfully");
        // setViewAddonDetailModal(false);
    }, [addonInsuranceSelectedId, individualAddonCosts]);
    const viewInsuranceBenefits = (type, benefits) => {

        setOptions(prev => ({
            ...prev,
            insuranceBenefits: benefits || []
        }));
        setViewInsuranceBenefit(true);
    };

    const handleSummaryBack = useCallback(() => {
        setIsPlanComponentVisible(true);
        setIsTravelSummaryVisible(false);
    }, []);
console.log("Rendering PremiumCalculator with state:", {form})
    return (
        <>

            {isTravelSummaryVisible ? (
                <>
                    <TravelSummary
                        Company={form.scheme?.label || "N/A"}
                        documentType={form.documentType?.label || "N/A"}
                        geographicalCover={form.geographicalCover?.label || "N/A"}
                        duration={form.travelDays ? `${form.travelDays} days` : "N/A"}
                        travellers={form.dob ? `DOB: ${form.dob}` : "N/A"}
                        age={form.age ? `${form.age} years` : "N/A"}
                        startDate={form.startDate || "N/A"}
                        endDate={form.endDate || "N/A"}
                        amtDays={form.amtDays ? `${form.amtDays} days` : "N/A"}
                        criticalIllness={form.criticalIllness || "N/A"}
                        preExistingDisease={form.preExistingDisease || "N/A"}

                        onBack={handleSummaryBack}
                    />
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: "12px",
                            flexWrap: "wrap",
                            marginBottom: "14px",
                        }}
                    >
                        {!isPlanComponentVisible && (
                            <Button
                                color="primary"
                                onClick={() => setIsPlanComponentVisible(true)}
                                style={{
                                    borderRadius: "999px",
                                    padding: "8px 16px",
                                    backgroundColor: "#0052a3",
                                    borderColor: "#0052a3",
                                    fontWeight: 600,
                                }}
                                onMouseEnter={(event) => {
                                    event.currentTarget.style.backgroundColor = "#003d7a";
                                    event.currentTarget.style.borderColor = "#003d7a";
                                }}
                                onMouseLeave={(event) => {
                                    event.currentTarget.style.backgroundColor = "#0052a3";
                                    event.currentTarget.style.borderColor = "#0052a3";
                                }}
                            >
                                View Plans
                            </Button>
                        )}
                    </div>

                    {isPlanComponentVisible && (
                        <Card className="mt-3" style={{ borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                            <CardBody>
                                <MembershipFee
                                    onBack={() => setIsPlanComponentVisible(false)}
                                    showBackButton={false}
                                    plans={plans}
                                    planCount={planCount}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                    activeSearchQuery={searchQuery}
                                    onSearchPlans={handlePlanSearch}
                                    isLoadingPlans={isPlansLoading}
                                    selectedPlan={selectedPlan}
                                    selectedPlanDetails={selectedPlanState}
                                    handleSelectPlan={handleSelectPlan}
                                    handleCoverChange={handleCoverChange}
                                    enquiryData={form}
                                    onShareQuoteSuccess={resetPlanValues}
                                />

                            </CardBody>
                        </Card>
                    )}
                </>
            ) :

                <Card className="height-equal step1" data-intro="This is where you can generate plan wise report" style={{ borderRadius: "18px", border: "1px solid #d9e4f4", boxShadow: "0 10px 30px rgba(2, 32, 71, 0.08)", overflow: "hidden" }}>
                    <CardBody className="tabbed-card body_with_low_padding" style={{ padding: "14px" }}>
                        <div className="premium-calculator">
                            <Row className="g-3 ">

                                <div className="col-12 col-lg-7"
                                    style={{
                                       
                                        height: "100%"   // ✅ IMPORTANT
                                    }}
                                >
                                    <h3 className="mb-1" style={{ fontWeight: 800, color: "#0f2744", fontSize: "20px" }}>Membership Fee Calculator</h3>
                                    <p className="mb-3" style={{ color: "#516a8f", fontWeight: 500 }}>Fill in the details below to calculate your membership fee estimate</p>

                                    <Form onSubmit={handleSubmit}>
                                        <div style={{ borderRadius: "12px", padding: "8px", marginBottom: "8px", background: "linear-gradient(135deg, rgb(21 77 133) 0%, rgb(0, 114, 223) 100%)", color: "#ffffff" }}>


                                            <h6 style={{ marginBottom: "12px", fontWeight: 700, fontSize: "14px" }}>Plan Details</h6>
                                            <Row>
                                                {DROPDOWN_CONFIG.map((config) => {
                                                    const isVisible = !config.dependsOn || form[config.dependsOn];
                                                    let dropdownOptions = [];
                                                    dropdownOptions = options[config.key] || [];
                                                    const isDisabled = config.dependsOn && !form[config.dependsOn];

                                                    return (
                                                        <div key={config.key} className="col-12 col-md-4">
                                                            {isVisible && (
                                                                <>
                                                                    <SelectComponent
                                                                        name={config.key}
                                                                        label={config.label}
                                                                        options={dropdownOptions}
                                                                        value={form[config.key]}
                                                                        onChange={handleChange}
                                                                        disabled={isDisabled}
                                                                        placeholder={config.placeholder}
                                                                    />
                                                                    {errors[config.key] && <div className="text-warning small">{errors[config.key]}</div>}
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </Row>
                                        </div>

                                        <Row className="">
                                            <div className="col-12 col-lg-12">
                                                <div style={{ borderRadius: "12px", padding: "8px", marginBottom: "8px", background: "linear-gradient(135deg, rgb(21 77 133) 0%, rgb(0, 114, 223) 100%)", color: "#ffffff" }}>
                                                    <h6 style={{ marginBottom: "12px", fontWeight: 700, fontSize: "14px" }}>Travel Information</h6>
                                                    <Row className="">
                                                        <div className="col-12 col-md-6 col-lg-4 mb-2">
                                                            <FormGroup>
                                                                <Label for="startDate">Start Date</Label>
                                                                <Input
                                                                    type="date"
                                                                    id="startDate"
                                                                    name="startDate"
                                                                    value={form.startDate}
                                                                    onChange={handleChange}
                                                                    min={todayDate}
                                                                    disabled={isAmtType}
                                                                    aria-label="Start Date"
                                                                    style={{ borderRadius: "10px" }}
                                                                />
                                                                {errors.startDate && <div className="text-warning small">{errors.startDate}</div>}
                                                            </FormGroup>
                                                        </div>
                                                        <div className="col-12 col-md-6 col-lg-4 mb-2">
                                                            <FormGroup>
                                                                <Label for="endDate">End Date</Label>
                                                                <Input
                                                                    type="date"
                                                                    id="endDate"
                                                                    name="endDate"
                                                                    value={form.endDate}
                                                                    onChange={handleChange}
                                                                    min={form.startDate || todayDate}
                                                                    disabled={isAmtType}
                                                                    aria-label="End Date"
                                                                    style={{ borderRadius: "10px" }}
                                                                />
                                                                {errors.endDate && <div className="text-warning small">{errors.endDate}</div>}
                                                            </FormGroup>
                                                        </div>
                                                        <div className="col-12 col-md-6 col-lg-4 mb-2">
                                                            <FormGroup>
                                                                <Label for="dob">Date of Birth</Label>
                                                                <Input
                                                                    type="date"
                                                                    id="dob"
                                                                    name="dob"
                                                                    value={form.dob}
                                                                    onChange={handleChange}
                                                                    max={todayDate}
                                                                    aria-label="Date of Birth"
                                                                    style={{ borderRadius: "10px" }}
                                                                />
                                                                {errors.dob && <div className="text-warning small">{errors.dob}</div>}
                                                            </FormGroup>
                                                        </div>
                                                        <div className="col-12 col-md-6 col-lg-4">
                                                            <FormGroup>
                                                                <Label for="travelDays">Travel Days</Label>
                                                                <Input
                                                                    type="number"
                                                                    id="travelDays"
                                                                    name="travelDays"
                                                                    value={form.travelDays}
                                                                    onChange={handleChange}
                                                                    placeholder="e.g., 30"
                                                                    min="1"
                                                                    disabled={isAmtType}
                                                                    aria-label="Travel Days"
                                                                    style={{ borderRadius: "10px" }}
                                                                />
                                                                {errors.travelDays && <div className="text-warning small">{errors.travelDays}</div>}
                                                            </FormGroup>
                                                        </div>
                                                        <div className="col-12 col-md-6 col-lg-4">
                                                            {isAmtType && (
                                                                <FormGroup>
                                                                    <Label for="amtDays">AMT Plan Days</Label>
                                                                    <Input
                                                                        type="select"
                                                                        id="amtDays"
                                                                        name="amtDays"
                                                                        value={form.amtDays}
                                                                        onChange={handleChange}
                                                                        aria-label="AMT Plan Days"
                                                                        style={{ borderRadius: "10px" }}
                                                                    >
                                                                        <option value="">Select AMT plan days</option>
                                                                        <option value="30">30</option>
                                                                        <option value="45">45</option>
                                                                        <option value="60">60</option>
                                                                    </Input>
                                                                    {errors.amtDays && <div className="text-warning small">{errors.amtDays}</div>}
                                                                </FormGroup>
                                                            )}
                                                        </div>
                                                        <div className="col-12 col-md-6 col-lg-4">
                                                             <div className="col-12 col-md-12">
                                                            <YesNoField
                                                                name="criticalIllness"
                                                                label="Critical Illness"
                                                                value={form.criticalIllness}
                                                                onChange={handleChange}
                                                                error={errors.criticalIllness}
                                                            />
                                                        </div>
                                                        </div>
                                                    </Row>
                                                </div>

                                            </div>
                                            {/* <div className="col-12 col-lg-4">
                                                <div style={{ borderRadius: "12px", padding: "10px", marginBottom: "8px", background: "linear-gradient(135deg, rgb(21 77 133) 0%, rgb(0, 114, 223) 100%)", color: "#ffffff" }}>
                                                    <h6 style={{ marginBottom: "12px", fontWeight: 700, fontSize: "10px" }}>Health Declarations</h6>
                                                    <Row>
                                                        <div className="col-12 col-md-12">
                                                            <YesNoField
                                                                name="criticalIllness"
                                                                label="Critical Illness"
                                                                value={form.criticalIllness}
                                                                onChange={handleChange}
                                                                error={errors.criticalIllness}
                                                            />
                                                        </div>
                                                        <div className="col-12 col-md-12">
                                                            <YesNoField
                                                                name="preExistingDisease"
                                                                label="Pre-existing Diseases"
                                                                value={form.preExistingDisease}
                                                                onChange={handleChange}
                                                                error={errors.preExistingDisease}
                                                            />
                                                        </div>
                                                    </Row>
                                                </div>
                                            </div> */}
                                        </Row>
                                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                                            <Button
                                                type="submit"
                                                size="lg"
                                                className="health-declaration-continue-btn"
                                                style={{ borderRadius: "999px", padding: "8px 20px", fontWeight: 700, fontSize: "14px" }}
                                            >
                                                Continue
                                            </Button>
                                        </div>
                                    </Form>
                                </div>
                                <div className="col-12 col-lg-5" >
                                </div>

                                {/* <div className="col-12 col-lg-6">
                                <div style={{ height: "100%", padding: "6px 4px" }}>
                                    <div style={{ minHeight: "320px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                                        <img
                                            src={travellerImage}
                                            alt="Traveller"
                                            style={{ width: "100%", maxWidth: "380px", height: "auto", objectFit: "contain" }}
                                        />
                                    </div>
                                </div>
                            </div> */}

                            </Row>
                        </div>
                    </CardBody>
                </Card>

            }
            {viewAddonDetailModal && (
                <AddonDetails
                    isOpen={viewAddonDetailModal}
                    toggle={() => setViewAddonDetailModal(false)}
                    AddonDetails={selectedAddonDetails}
                    setSelectedAddonDetails={setSelectedAddonDetails}
                    individualCosts={individualAddonCostsBYPlanId[selectedPlanIdForAddon] || {}}
                    additionalCOACost={additionalCOACost}
                    planCost={baseCost || selectedPlanState[selectedPlanIdForAddon]?.cost || selectedPlanState?.cost}
                    planCOICost={coiCost || planCOICost}
                    currentCover={selectedPlanState?.cover}
                    planId={selectedPlanIdForAddon}
                    removeAddon={removeAddonInd}
                    handleAddonSelect={handleAddonSelectInd}
                />
            )}
            {isViewInsurancebenefit && (
                <InsuranceBenefit
                    isOpen={isViewInsurancebenefit}
                    toggle={() => setViewInsuranceBenefit(false)}
                    insuranceBenefits={options?.insuranceBenefits || []}
                    name={"Benefits"}
                    type={form?.scheme?.label}
                />
            )}

        </>

    );
};

export default PremiumCalculator;
